import React from "react";
import { Text, View } from "@react-pdf/renderer";
import { withNamespaces } from "react-i18next";
import styles from "../../styles";
import { chunkTableRows, formatDisplayValue, roundedCornerStyle, roundedRowStyle } from "../../helpers";

const ROWS_PER_PAGE = 22;
const TABLE_MIN_PRESENCE = 100;

const KeyValueTable = ({
    t,
    rows = [],
    breakBefore = false,
    emptyMessage = "No data available",
    titleColumnLabel = "Title",
    detailsColumnLabel = "Information",
    leading = null,
}) => {
    const hasRows = rows.length > 0;
    const displayRows = hasRows
        ? rows
        : [{ title: emptyMessage, details: "-", __empty: true }];

    const chunks = chunkTableRows(displayRows, ROWS_PER_PAGE);

    const tables = chunks.map((chunk, chunkIndex) => {
        const isFirstChunk = chunkIndex === 0;

        return (
            <View
                key={`kv-table-chunk-${chunkIndex}`}
                wrap={false}
                minPresenceAhead={isFirstChunk ? TABLE_MIN_PRESENCE : 80}
                {...(breakBefore && isFirstChunk ? { break: true } : {})}
            >
                {isFirstChunk ? leading : null}
                <View style={styles.kvTable}>
                    <View style={{ ...styles.kvTableRow, ...roundedRowStyle({ isFirstRow: true }) }}>
                        <View
                            style={{
                                ...styles.kvTableTitleCol,
                                ...styles.wordTableHeaderCell,
                                ...roundedCornerStyle({ isFirstRow: true, isFirstCol: true }),
                            }}
                        >
                            <Text style={styles.wordTableHeaderText}>
                                {t(titleColumnLabel)}
                            </Text>
                        </View>
                        <View
                            style={{
                                ...styles.kvTableDetailCol,
                                ...styles.wordTableHeaderCell,
                                ...roundedCornerStyle({ isFirstRow: true, isLastCol: true }),
                            }}
                        >
                            <Text style={styles.wordTableHeaderText}>
                                {t(detailsColumnLabel)}
                            </Text>
                        </View>
                    </View>

                    {chunk.map((row, index) => {
                        const absoluteIndex = (chunkIndex * ROWS_PER_PAGE) + index;
                        const isLast = index === chunk.length - 1;
                        const isAlt = absoluteIndex % 2 === 1;
                        const rowStyle = {
                            ...styles.kvTableRow,
                            ...(isAlt ? styles.tableRowAlt : {}),
                            ...(isLast ? styles.kvTableLastRow : {}),
                            ...roundedRowStyle({ isLastRow: isLast }),
                        };

                        if (row.__empty) {
                            return (
                                <View style={rowStyle} key={`kv-empty-${absoluteIndex}`}>
                                    <View
                                        style={{
                                            ...styles.kvTableDetailCol,
                                            width: "100%",
                                            ...(isAlt ? styles.tableRowAlt : {}),
                                            ...roundedCornerStyle({
                                                isLastRow: isLast,
                                                isFirstCol: true,
                                                isLastCol: true,
                                            }),
                                        }}
                                    >
                                        <Text style={{ ...styles.kvTableCell, ...styles.kvTableKeyText }}>
                                            {t(row.title)}
                                        </Text>
                                    </View>
                                </View>
                            );
                        }

                        return (
                            <View
                                style={rowStyle}
                                key={`${row.title}-${absoluteIndex}`}
                            >
                                <View
                                    style={{
                                        ...styles.kvTableTitleCol,
                                        ...(isAlt ? styles.tableRowAlt : {}),
                                        ...roundedCornerStyle({
                                            isLastRow: isLast,
                                            isFirstCol: true,
                                        }),
                                    }}
                                >
                                    <Text style={{ ...styles.kvTableCell, ...styles.kvTableKeyText }}>
                                        {t(row.title)}
                                    </Text>
                                </View>
                                <View
                                    style={{
                                        ...styles.kvTableDetailCol,
                                        ...(isAlt ? styles.tableRowAlt : {}),
                                        ...roundedCornerStyle({
                                            isLastRow: isLast,
                                            isLastCol: true,
                                        }),
                                    }}
                                >
                                    <Text style={{ ...styles.kvTableCell, ...styles.kvTableDetailText }}>
                                        {formatDisplayValue(row.details)}
                                    </Text>
                                </View>
                            </View>
                        );
                    })}
                </View>
            </View>
        );
    });

    return <View>{tables}</View>;
};

export default withNamespaces()(KeyValueTable);
